import React, { useEffect, useState } from 'react';
import { EditorService } from '../../services/editor';
import Dialog from '../../lib/dialog/Dialog';
import PublicationPreviewDialog from './preview/PublicationPreviewDialog';
import { Route, Routes, useParams } from 'react-router-dom';
import { popupNotification } from '../../popup-notification';
import PublicationHistoryDialog from './history/PublicationHistoryDialog';
import PublishingSequenceDialog from './publication-sequence/PublishingSequenceDialog';
import { Logger } from '@elyx-code/common-ts-utils';
import DialogLoader from '../DialogLoader';

export interface IPublishingProps {
	onClose: (event?: any) => void;
	contentHeight?: boolean;
	project: EditorService;
}

const CreatePublicationRedirect: React.FC<IPublishingProps> = ({
	project,
	onClose
}) => {
	useEffect(() => {
		project?.publish();
	}, [project]);

	return (
		<Dialog onClose={onClose}>
			<DialogLoader
				onClose={onClose}
				title="Starting new publication"
				subtitle="Publication sequence"
				loadingMessage="Loading..."
			/>
		</Dialog>
	);
};

const LoadPublicationAndRender: React.FC<
	IPublishingProps & { ready: boolean }
> = ({ project, onClose, ready }) => {
	const { publicationId } = useParams<{ publicationId: string }>();
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!publicationId) return;

		if (!ready) return;

		const existing = project.publication;
		if (existing) {
			setLoading(false);
		} else {
			project
				.loadGivenPublication(publicationId)
				.then(() => {
					setLoading(false);
				})
				.catch((err) => {
					Logger.error('couldn’t load publication', err);
					popupNotification({
						text: 'Couldn’t load publication',
						color: 'error',
						delay: 2000
					});
					onClose();
				});
		}
	}, [publicationId, project, ready]);

	if (loading || !ready) {
		return (
			<Dialog onClose={onClose}>
				<DialogLoader
					onClose={onClose}
					title="Project publication"
					subtitle="Publication sequence"
					loadingMessage="Loading..."
				/>
			</Dialog>
		);
	}

	return (
		<Dialog onClose={onClose}>
			<PublishingSequenceDialog project={project} onClose={onClose} />
		</Dialog>
	);
};

const Publication: React.FC<IPublishingProps & { ready: boolean }> = ({
	onClose,
	project,
	ready
}) => (
	<Routes>
		<Route
			path="preview"
			element={
				<Dialog onClose={onClose}>
					<PublicationPreviewDialog
						project={project}
						onClose={onClose}
					/>
				</Dialog>
			}
		/>
		<Route
			path="history"
			element={
				<Dialog onClose={onClose}>
					<PublicationHistoryDialog
						project={project}
						onClose={onClose}
					/>
				</Dialog>
			}
		/>
		<Route
			path="publication-sequence/new"
			// Create a new publication before redirecting to `publication-sequence/:publicationId`
			element={
				<CreatePublicationRedirect
					project={project}
					onClose={onClose}
				/>
			}
		/>
		<Route
			path="publication-sequence/:publicationId"
			// Check if a publication with this id is already in state, otherwise fetch it, before rendering the component
			element={
				<LoadPublicationAndRender
					project={project}
					onClose={onClose}
					ready={ready}
				/>
			}
		/>
	</Routes>
);

export default Publication;
